/**
 * Parses a number a person typed. Understands thousands separators (1,000 / 1,00,000 / 1 000 / 1.000.000),
 * a decimal comma (1.250,50 / 3,5) and a decimal point (1,250.50). Returns null for empty or non-numeric text.
 */
export function parseNumber(raw: string): number | null {
  // A currency or percent sign around the number ("$400,000", "6.5%", "€1.250,50") is not part of the value.
  raw = raw.trim().replace(/^([-+−]?)\s*[$€£¥₹]\s*/, "$1").replace(/\s*[%$€£¥₹]$/, "");
  let s = raw.trim().replace(/[\s_  ']/g, "");
  if (!s) return null;
  let sign = 1;
  if (s.startsWith("-") || s.startsWith("−")) { sign = -1; s = s.slice(1); } else if (s.startsWith("+")) s = s.slice(1);
  if (!/^[\d.,]+$/.test(s) || !/\d/.test(s)) return null;
  const lastDot = s.lastIndexOf("."), lastComma = s.lastIndexOf(",");
  let out: string;
  if (lastDot >= 0 && lastComma >= 0) {
    const dec = lastDot > lastComma ? "." : ",";
    const grp = dec === "." ? "," : ".";
    if (s.split(dec).length > 2) return null;
    out = s.split(grp).join("").replace(dec, ".");
  } else if (lastComma >= 0 || lastDot >= 0) {
    const sep = lastComma >= 0 ? "," : ".";
    const parts = s.split(sep);
    const groupedThousands = /^\d{1,3}$/.test(parts[0]) && parts.slice(1).every((p, i, a) => (i === a.length - 1 ? /^\d{3}$/.test(p) : /^\d{2,3}$/.test(p)));
    if (parts.length > 2) { if (!groupedThousands) return null; out = parts.join(""); }
    else if (groupedThousands && sep === ",") out = parts.join("");
    else out = parts.join(".");
  } else out = s;
  const n = Number(out);
  return Number.isFinite(n) ? sign * n : null;
}

/** Friendly number text: thousands separators, no needless trailing zeros. */
export function fmt(n: number, maxDecimals = 6): string {
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-US", { maximumFractionDigits: maxDecimals });
}

/** Drop-in for parseFloat on typed text: reads "1,200", "1.250,50" and "3,5" correctly. NaN when unreadable, like parseFloat. */
export function pnum(raw: unknown): number {
  const n = parseNumber(String(raw ?? ""));
  return n === null ? NaN : n;
}
